<template>
    <div class="z-50">
        <ProgressModal :visible="isLoading" :message="loadingMessage" :progress="loadingProgress" />
        <WelcomeModal />

        <FloatingPopup :width="800" :height="600" id="mqtt"
            :modelValue="showMqttPopup" @update:modelValue="$emit('update:showMqttPopup', $event)"
            title="MQTT monitor">
            <MqttTab :popupState="showMqttPopup" :settings="appSettings" />
        </FloatingPopup>

        <FloatingPopup :width="800" :height="600" id="export"
            :modelValue="showJsonlPopup" @update:modelValue="$emit('update:showJsonlPopup', $event)"
            title="Export plate">
            <ExportTab :popupState="showJsonlPopup" :layer="layer" :settings="appSettings"
                :layerUpdate="layerUpdate" />
        </FloatingPopup>

        <FloatingPopup :width="800" :height="600" id="hass-export"
            :modelValue="showHassExportPopup" @update:modelValue="$emit('update:showHassExportPopup', $event)"
            title="Export Homeassistant Yaml">
            <HassConfigTab :popupState="showHassExportPopup" :layer="layer" :settings="appSettings"
                :hassConfigUpdate="layerUpdate" />
        </FloatingPopup>

        <FloatingPopup :width="1200" :height="700" id="scripts"
            :modelValue="showScriptsPopup" @update:modelValue="$emit('update:showScriptsPopup', $event)"
            title="Script Editor">
            <ScriptsTab :popupState="showScriptsPopup" :layer="layer" :settings="appSettings" />
        </FloatingPopup>

        <FloatingPopup :width="600" :height="400" id="ftp"
            :modelValue="showFTPPopup" @update:modelValue="$emit('update:showFTPPopup', $event)"
            title="Sync Resource Files">
            <FtpExportTab :popupState="showFTPPopup" :layer="layer" :settings="appSettings"
                :layerUpdate="layerUpdate" :imageStoreUpdated="imageStoreUpdated"
                @imageStoreUpdated="$emit('image-added')" />
        </FloatingPopup>

        <FloatingPopup :width="600" :height="600" id="telnet"
            :modelValue="showTelnetPopup" @update:modelValue="$emit('update:showTelnetPopup', $event)"
            title="Telnet to Plate">
            <TelnetTerminal :popupState="showTelnetPopup" :settings="appSettings" />
        </FloatingPopup>

        <!-- SETTINGS POPUP -->
        <FloatingPopup :width="600" :height="800" id="settings"
            :modelValue="showSettingsPopup" @update:modelValue="$emit('update:showSettingsPopup', $event)"
            title="Settings">
            <AppSettings
                :appSettings="appSettings"
                :testingMqtt="testingMqtt"
                :mqttTestStatus="mqttTestStatus"
                :mqttTestMessage="mqttTestMessage"
                :testingHass="testingHass"
                :hassTestStatus="hassTestStatus"
                :hassTestMessage="hassTestMessage"
                :testingSftp="testingSftp"
                :sftpTestStatus="sftpTestStatus"
                :sftpTestMessage="sftpTestMessage"
                :testingGithub="testingGithub"
                :githubTestStatus="githubTestStatus"
                :githubTestMessage="githubTestMessage"
                @saveSettings="$emit('save-settings', $event)"
                @updateZoomSensitivity="$emit('update-zoom-sensitivity')"
                @updateConnectionSpacing="$emit('update-connection-spacing')"
                @testMqttConnection="$emit('test-mqtt-connection')"
                @testHassConnection="$emit('test-hass-connection')"
                @testSftpConnection="$emit('test-sftp-connection')"
                @testGithubConnection="$emit('test-github-connection')"
                @loadSettingsFromFile="$emit('load-settings-from-file', $event)"
            />
        </FloatingPopup>

        <!-- IMAGE STORE POPUP -->
        <FloatingPopup :width="800" :height="600" id="image-store"
            :modelValue="showImageTab" @update:modelValue="$emit('update:showImageTab', $event)"
            title="Image store">
            <ImageTab @imageDeleted="$emit('image-deleted', $event)" @imageUpload="$emit('image-added')"
                :refreshTrigger="imageStoreUpdated" />
        </FloatingPopup>

        <!-- PLATES CONFIGURATION POPUP -->
        <FloatingPopup :width="1200" :height="700" id="plates"
            :modelValue="showPlatesPopup" @update:modelValue="$emit('update:showPlatesPopup', $event)"
            title="Plates Configuration">
            <PlateProperties
                :plates="plates"
                :appSettings="appSettings"
                :popupState="showPlatesPopup"
                @update="$emit('update-plates', $event)"
                @update_active_plate="$emit('update-active-plate', $event)"
                @imageStoreUpdated="$emit('image-added')"
            />
        </FloatingPopup>

        <!-- PLATE TYPES EDITOR POPUP -->
        <FloatingPopup :width="900" :height="650" id="plate-types"
            :modelValue="showPlateTypesPopup" @update:modelValue="$emit('update:showPlateTypesPopup', $event)"
            title="Plate Types Editor">
            <PlateTypesComponent />
        </FloatingPopup>

        <ConfirmationModal :show="showDeleteModal" title="Delete selected object"
            message="Are you sure you want to delete this object?"
            @confirm="$emit('delete-confirm')" @cancel="$emit('delete-cancel')"
            width="small" />

        <ConfirmationModal :show="showNewProjectModal" title="New Project!"
            message="Your current design will be lost.<br><p>Please save your work first.</p>"
            @confirm="$emit('new-project-confirm')"
            @cancel="$emit('update:showNewProjectModal', false)"
            width="small" />

        <SaveDesignDialog :visible="showSaveDesignPrompt" :filenamePreset="saveDesignFilename"
            :currentMajor="designMajor" :currentMinor="designMinor" :versionHistory="designVersionHistory"
            @update:visible="$emit('update:showSaveDesignPrompt', $event)"
            @save="$emit('save-design', $event)" />

        <!-- CUSTOM COMPONENT SAVE DIALOG -->
        <SaveCustomComponentDialog
            :visible="showSaveCustomComponentDialog"
            :thumbnail="saveCustomComponentThumbnail"
            @save="$emit('save-custom-component', $event)"
            @cancel="$emit('update:showSaveCustomComponentDialog', false)" />

        <!-- CUSTOM COMPONENT MANAGER -->
        <FloatingPopup :width="700" :height="600" id="custom-components"
            :modelValue="showCustomManagerPopup" @update:modelValue="$emit('update:showCustomManagerPopup', $event)"
            title="Custom Component Manager">
            <CustomComponentManager :refreshTrigger="customManagerRefresh"
                @updated="$emit('custom-components-updated')"
                @edit-design="$emit('edit-design', $event)" />
        </FloatingPopup>

        <!-- DOCUMENTATION VIEWER -->
        <FloatingPopup :width="900" :height="650" id="docs"
            :modelValue="showDocsPopup" @update:modelValue="$emit('update:showDocsPopup', $event)"
            title="Documentation">
            <HelpViewer />
        </FloatingPopup>

        <!-- GITHUB SYNC MODAL -->
        <GitHubSyncModal
            :visible="showGitHubSyncModal"
            :settings="appSettings"
            :designId="designId"
            :designName="designFilename"
            :buildPackage="buildPackage"
            @update:visible="$emit('update:showGitHubSyncModal', $event)"
            @load-package="(pkg) => $emit('github-load-package', pkg)"
            @package-saved="(pkg) => $emit('package-saved', pkg)"
        />

        <!-- IMPORT ISSUES -->
        <FloatingPopup :width="520" :height="360" id="import-issues"
            :modelValue="showImportIssuesPopup" @update:modelValue="$emit('update:showImportIssuesPopup', $event)"
            title="Import completed with warnings">
            <div class="flex flex-col gap-3 p-1">
                <p class="text-sm text-slate-300">The design was imported, but some issues were found:</p>
                <ul class="flex flex-col gap-2 overflow-y-auto" style="max-height: 220px;">
                    <li v-for="(issue, i) in importIssues" :key="i"
                        class="flex items-start gap-2 rounded-md px-3 py-2"
                        :class="issue.severity === 'error' ? 'bg-red-900/40 border border-red-700/50' : 'bg-yellow-900/30 border border-yellow-700/40'">
                        <span class="mt-0.5 shrink-0 text-base leading-none"
                            :class="issue.severity === 'error' ? 'text-red-400' : 'text-yellow-400'">
                            {{ issue.severity === 'error' ? '✕' : '⚠' }}
                        </span>
                        <div class="min-w-0">
                            <div class="text-sm font-medium text-white truncate">{{ issue.message }}</div>
                            <div v-if="issue.detail" class="text-xs text-slate-400 mt-0.5">{{ issue.detail }}</div>
                        </div>
                    </li>
                </ul>
                <div class="flex justify-end pt-1">
                    <button @click="$emit('update:showImportIssuesPopup', false)"
                        class="px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
                        Dismiss
                    </button>
                </div>
            </div>
        </FloatingPopup>
    </div>
</template>

<script>
import ProgressModal from './ProgressModal.vue';
import WelcomeModal from './WelcomeModal.vue';
import FloatingPopup from './FloatingPopup.vue';
import ConfirmationModal from './ConfirmationModal.vue';
import AppSettings from './AppSettings.vue';
import ImageTab from './imagemodal/ImageTab.vue';
import MqttTab from './export/MqttTab.vue';
import ExportTab from './export/ExportTab.vue';
import FtpExportTab from './export/FtpExportTab.vue';
import HassConfigTab from './export/HassConfigTab.vue';
import TelnetTerminal from './telnet/TelnetTerminal.vue';
import SaveDesignDialog from './export/SaveDesignDialog.vue';
import PlateProperties from './properties/plate/PlateProperties.vue';
import PlateTypesComponent from './properties/plate/PlateTypesComponent.vue';
import SaveCustomComponentDialog from './customcomponents/SaveCustomComponentDialog.vue';
import CustomComponentManager from './customcomponents/CustomComponentManager.vue';
import ScriptsTab from '../flow/ScriptsTab.vue';
import HelpViewer from './help/HelpViewer.vue';
import GitHubSyncModal from './github/GitHubSyncModal.vue';

export default {
    name: 'AppModals',
    components: {
        ProgressModal,
        WelcomeModal,
        FloatingPopup,
        ConfirmationModal,
        AppSettings,
        ImageTab,
        MqttTab,
        ExportTab,
        FtpExportTab,
        HassConfigTab,
        TelnetTerminal,
        SaveDesignDialog,
        PlateProperties,
        PlateTypesComponent,
        SaveCustomComponentDialog,
        CustomComponentManager,
        ScriptsTab,
        HelpViewer,
        GitHubSyncModal,
    },
    props: {
        // Loading state
        isLoading: { type: Boolean, default: false },
        loadingMessage: { type: String, default: '' },
        loadingProgress: { type: Number, default: null },
        // Shared data
        appSettings: { type: Object, required: true },
        layer: { type: Object, default: undefined },
        layerUpdate: { type: Number, default: 0 },
        imageStoreUpdated: { type: Number, default: 0 },
        plates: { type: Array, default: undefined },
        // Connection test states
        testingMqtt: { type: Boolean, default: false },
        mqttTestStatus: { type: String, default: null },
        mqttTestMessage: { type: String, default: '' },
        testingHass: { type: Boolean, default: false },
        hassTestStatus: { type: String, default: null },
        hassTestMessage: { type: String, default: '' },
        testingSftp: { type: Boolean, default: false },
        sftpTestStatus: { type: String, default: null },
        sftpTestMessage: { type: String, default: '' },
        // Popup visibility (v-model)
        showMqttPopup: { type: Boolean, default: false },
        showJsonlPopup: { type: Boolean, default: false },
        showHassExportPopup: { type: Boolean, default: false },
        showScriptsPopup: { type: Boolean, default: false },
        showFTPPopup: { type: Boolean, default: false },
        showTelnetPopup: { type: Boolean, default: false },
        showSettingsPopup: { type: Boolean, default: false },
        showImageTab: { type: Boolean, default: false },
        showPlatesPopup: { type: Boolean, default: false },
        showPlateTypesPopup: { type: Boolean, default: false },
        showDeleteModal: { type: Boolean, default: false },
        showNewProjectModal: { type: Boolean, default: false },
        showSaveDesignPrompt: { type: Boolean, default: false },
        saveDesignFilename: { type: String, default: '' },
        designMajor: { type: Number, default: 0 },
        designMinor: { type: Number, default: 0 },
        designVersionHistory: { type: Array, default: () => [] },
        showSaveCustomComponentDialog: { type: Boolean, default: false },
        saveCustomComponentThumbnail: { type: String, default: null },
        showCustomManagerPopup: { type: Boolean, default: false },
        showDocsPopup: { type: Boolean, default: false },
        showImportIssuesPopup: { type: Boolean, default: false },
        importIssues: { type: Array, default: () => [] },
        customManagerRefresh: { type: Number, default: 0 },
        // GitHub sync
        showGitHubSyncModal: { type: Boolean, default: false },
        designId: { type: String, default: '' },
        designFilename: { type: String, default: '' },
        buildPackage: { type: Function, default: null },
        testingGithub: { type: Boolean, default: false },
        githubTestStatus: { type: String, default: null },
        githubTestMessage: { type: String, default: '' },
    },
    emits: [
        'update:showMqttPopup', 'update:showJsonlPopup', 'update:showHassExportPopup',
        'update:showScriptsPopup', 'update:showFTPPopup', 'update:showTelnetPopup',
        'update:showSettingsPopup', 'update:showImageTab', 'update:showPlatesPopup',
        'update:showPlateTypesPopup', 'update:showDeleteModal', 'update:showNewProjectModal',
        'update:showSaveDesignPrompt', 'update:showSaveCustomComponentDialog',
        'update:showCustomManagerPopup', 'update:showDocsPopup', 'update:showImportIssuesPopup',
        // Settings actions
        'save-settings', 'update-zoom-sensitivity', 'update-connection-spacing',
        'test-mqtt-connection', 'test-hass-connection', 'test-sftp-connection',
        'load-settings-from-file',
        // Image store
        'image-deleted', 'image-added',
        // Plates
        'update-plates', 'update-active-plate',
        // Confirmation modals
        'delete-confirm', 'delete-cancel',
        'new-project-confirm',
        // Design save
        'save-design',
        // Custom components
        'save-custom-component', 'custom-components-updated', 'edit-design',
        // GitHub sync
        'update:showGitHubSyncModal', 'github-load-package', 'package-saved', 'test-github-connection',
    ],
};
</script>
